// src/pages/ScanQRPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useProgram } from '../context/ProgramContext';
import apiClient from '../api';
import '../styles/ScanQRPage.css';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { PeraWalletConnect } from '@perawallet/connect';
import algosdk from 'algosdk';

const peraWallet = new PeraWalletConnect();

// Accept any non-empty scanned text. The user app encodes either a 58-char Algorand
// address (Pera / custodial wallet holders) or the participant's user_identifier
// (off-chain email/Google sign-ups who have no wallet). The backend resolves both
// via the shared participant-lookup helper.
const isValidScanResult = (text) => {
    if (typeof text !== 'string') return false;
    const trimmed = text.trim();
    return trimmed.length > 0 && trimmed.length <= 256;
};
const isAlgorandAddress = (text) => typeof text === 'string' && /^[A-Z2-7]{58}$/.test(text);

const ScanQRPage = ({ isAuthenticated }) => {
    const { selectedProgram } = useProgram();
    const [scanResult, setScanResult] = useState(null); // Raw scanned text
    const [scannerError, setScannerError] = useState(null);
    // const [manualInput, setManualInput] = useState(''); // Kept for potential future use

    const [scannedAddress, setScannedAddress] = useState(null); // Extracted ALGO address
    const [pointsToAdd, setPointsToAdd] = useState('');
    const [scanValidationError, setScanValidationError] = useState(''); // Error validating scanned data
    const [isSubmittingPoints, setIsSubmittingPoints] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    const scannerRef = useRef(null); // To hold the Html5QrcodeScanner instance
    const scannerRegionId = "qr-reader-region"; // ID for the div where scanner UI renders

    // --- Stop Scanner Callback (Robust) ---
    const stopScanner = useCallback(() => {
        if (scannerRef.current && typeof scannerRef.current.clear === 'function') {
            const scannerInstanceToClear = scannerRef.current;
            scannerRef.current = null; 
            console.log("Attempting to stop scanner instance (ref is now null):", scannerInstanceToClear);

            try {
                const clearPromise = scannerInstanceToClear.clear();
                if (clearPromise && typeof clearPromise.then === 'function') {
                    clearPromise.then(
                        () => { console.log("Scanner cleared successfully (Promise resolved)."); },
                        (err) => { console.error("Scanner clear() promise was rejected:", err); }
                    );
                } else {
                    console.log("Scanner clear() did not return a promise. Assuming synchronous clear or instance was invalid.");
                }
            } catch (error) {
                console.error("Synchronous error calling scannerInstanceToClear.clear():", error);
            }
        }
    }, []);


    // --- Start Scanner Callback (Corrected render call) ---
    const startScanner = useCallback(() => {
        console.log("Attempting to start scanner...");
        setScanResult(null); setScannedAddress(null); setScannerError(null);
        setScanValidationError(''); setPointsToAdd(''); setSubmitMessage('');

        const scannerElement = document.getElementById(scannerRegionId);
        if (!scannerElement) {
            console.error(`Scanner region element '${scannerRegionId}' not found.`);
            setScannerError("Scanner UI element missing. Please ensure the div exists or refresh the page.");
            return;
        }
        scannerElement.innerHTML = ''; 

        if (scannerRef.current) {
            console.warn("Existing scanner instance found in ref before starting new. Attempting to clear old one.");
            stopScanner();
        }

        try {
            const html5QrCodeScanner = new Html5QrcodeScanner(
                scannerRegionId,
                { fps: 10, qrbox: { width: 250, height: 250 }, rememberLastUsedCamera: true },
                false // verbose logging from the library
            );

            const onScanSuccessCallback = (decodedText, decodedResult) => {
                console.log(`Scan successful: ${decodedText}`);
                setScanResult(decodedText);
                setScannerError(null);
                setScanValidationError('');

                if (isValidScanResult(decodedText)) {
                    const trimmed = decodedText.trim();
                    setScannedAddress(trimmed);
                    console.log(
                        isAlgorandAddress(trimmed)
                            ? `Validated Algorand address: ${trimmed}`
                            : `Validated participant identifier: ${trimmed}`,
                    );
                    stopScanner();
                } else {
                    console.error("Empty / oversized QR payload:", decodedText);
                    setScanValidationError(`QR code is empty or unreadable. Please scan a valid participant QR.`);
                    setScannedAddress(null);
                }
            };

            const onScanFailureCallback = (error) => {
                // console.warn(`Scan Failure (usually ignored): ${error}`);
            };

            // Render the scanner. This method is void and does not return a promise.
            // Errors during this setup (e.g., camera not found, permissions denied by user at prompt)
            // are typically handled by the library or browser, often by not starting the video feed
            // or by the onScanFailure callback being invoked with an error.
            html5QrCodeScanner.render(onScanSuccessCallback, onScanFailureCallback);
            
            // If render itself throws a synchronous error (e.g., bad config), the outer try/catch will get it.
            
            scannerRef.current = html5QrCodeScanner; // Store the new instance
            console.log("Html5QrcodeScanner.render() called. Scanner should be attempting to start.");

        } catch (initializationError) {
            console.error("Error during Html5QrcodeScanner construction or initial render call:", initializationError);
            setScannerError(`Failed to create or render scanner: ${initializationError.message || initializationError}`);
        }
    }, [stopScanner]); // stopScanner is a stable callback

    // --- Effect to Start/Stop Scanner based on conditions ---
    useEffect(() => {
        if (isAuthenticated && selectedProgram && !scannedAddress) {
            startScanner();
        }
        return () => {
            console.log("useEffect cleanup: Calling stopScanner.");
            stopScanner();
        };
    }, [isAuthenticated, selectedProgram, scannedAddress, startScanner, stopScanner]);

    // --- Handler for Points Input ---
    const handlePointsChange = (event) => {
        const value = event.target.value;
        if (value === '') { setPointsToAdd(''); return; }
        if (selectedProgram?.asset_type === 'points_on_chain') {
            // Allow decimals for on-chain ASAs (the backend rounds to base units using asa_decimals)
            if (/^\d{0,9}(\.\d{0,8})?$/.test(value)) setPointsToAdd(value);
        } else if (/^[0-9]\d*$/.test(value) && parseInt(value, 10) <= 1000000) {
            setPointsToAdd(value);
        }
    };

    const isOnChain = selectedProgram?.asset_type === 'points_on_chain';

    const submitOffChain = async (pointsValue) => {
        const payload = {
            program_id: selectedProgram.id,
            algorand_address: scannedAddress,
            points_to_add: pointsValue,
        };
        const response = await apiClient.post('/participants/add_points', payload);
        setSubmitMessage(`Success! Added ${pointsValue} points to ${scannedAddress.substring(0, 6)}.... New balance: ${response.data.points_balance}`);
    };

    const submitOnChain = async (amountDisplay) => {
        // 1) Reconnect Pera (admin's wallet)
        let address = null;
        try {
            const reconnect = await peraWallet.reconnectSession();
            if (reconnect && reconnect.length) address = reconnect[0];
        } catch { /* ignore */ }
        if (!address) {
            try { await peraWallet.disconnect(); } catch { /* nothing to disconnect */ }
            const accounts = await peraWallet.connect();
            address = accounts[0];
        }
        if (!address) throw new Error('Pera Wallet not connected.');

        // 2) Ask backend to build the unsigned txn
        setSubmitMessage('Preparing on-chain transfer…');
        const prep = await apiClient.post('/participants/award-asa/prepare', {
            program_id: selectedProgram.id,
            participant_lookup: scannedAddress,
            amount_display: amountDisplay,
        });
        const { unsigned_txn_b64, participant_id, asa_decimals, amount_base_units } = prep.data;

        // 3) Decode base64 → bytes → Transaction → Pera signs
        const bytes = Uint8Array.from(atob(unsigned_txn_b64), (c) => c.charCodeAt(0));
        const txn = algosdk.decodeUnsignedTransaction(bytes);
        setSubmitMessage('Open Pera Wallet to confirm the transfer…');
        const signedTxns = await peraWallet.signTransaction([[{ txn, signers: [address] }]]);
        // peraWallet.signTransaction returns Uint8Array[] — one signed txn per input
        const signed_b64 = btoa(String.fromCharCode(...signedTxns[0]));

        // 4) Submit to backend
        setSubmitMessage('Submitting to Algorand…');
        const submit = await apiClient.post('/participants/award-asa/submit', {
            program_id: selectedProgram.id,
            participant_id,
            signed_txn_b64: signed_b64,
        });
        const display = (amount_base_units / Math.pow(10, asa_decimals)).toString();
        setSubmitMessage(`Success! Sent ${display} ${selectedProgram.asa_unit_name || 'units'} (txid ${submit.data.txid.slice(0, 10)}…).`);
    };

    const handleSubmitPoints = async (event) => {
        event.preventDefault();
        const numericValue = parseFloat(pointsToAdd);
        if (!scannedAddress || !pointsToAdd || !(numericValue > 0)) {
            setSubmitMessage('Error: Please enter a valid positive amount.');
            return;
        }
        console.log(`Submitting ${numericValue} for address ${scannedAddress} in program ${selectedProgram.id} (on-chain=${isOnChain})`);
        setIsSubmittingPoints(true);
        setSubmitMessage('');
        try {
            if (isOnChain) {
                await submitOnChain(numericValue);
            } else {
                await submitOffChain(parseInt(pointsToAdd, 10));
            }
            setPointsToAdd('');
        } catch (err) {
            console.error('Award failed:', err);
            const errorMsg = err.response?.data?.detail || err.message || 'Failed to award.';
            setSubmitMessage(`Error: ${errorMsg}`);
        } finally {
            setIsSubmittingPoints(false);
        }
    };

    // --- Handler to Restart Scan ---
    const handleScanAgain = () => {
        setScanResult(null);
        setScannedAddress(null); 
        setScanValidationError('');
        setPointsToAdd('');
        setSubmitMessage('');
    };

    // --- Authentication & Program Selection Checks ---
    if (!isAuthenticated) { return <Navigate to="/dashboard" replace />; }
    if (!selectedProgram) { return ( <div className="scan-qr-container info-message"> <h2>No Program Selected</h2> <p>Please <Link to="/select-program">select a program</Link> first.</p> </div> ); }

    // --- Render Page Content ---
    return (
        <div className="scan-qr-container">
            <h2>Scan Participant QR Code</h2>
            <p>Scan participant's Algorand Address QR for the <strong>{selectedProgram.name}</strong> program.</p>

            {!scannedAddress && (
                 <div id={scannerRegionId} className="scanner-view">
                    {!scannerError && <p>Place QR code within the frame...</p>}
                    {scannerError && <p className="error-message">Scanner Error: {scannerError}</p>}
                 </div>
            )}
            {scanValidationError && !scannedAddress && <p className="error-message">{scanValidationError}</p>}

            {scannedAddress && (
                <div className="scan-results-and-points">
                    <div className="scan-result-area">
                        <h2>Scan Result</h2>
                        <p>Participant Address:</p>
                        <p className="identifier-display">{scannedAddress}</p>
                        {scanResult !== scannedAddress && <p className="raw-scan-data">(Raw Scanned: {scanResult})</p> }
                        <button onClick={handleScanAgain} disabled={isSubmittingPoints}> Scan Another Code </button>
                    </div>
                    <div className="add-points-area">
                        <h2>{isOnChain ? `Send ${selectedProgram.asa_unit_name || 'tokens'}` : 'Add Points'}</h2>
                        {isOnChain && (
                            <p className="info-message">
                                On-chain transfer. Your Pera wallet will pay the network fee.
                                Decimals: {selectedProgram.asa_decimals ?? 0}.
                            </p>
                        )}
                        <form onSubmit={handleSubmitPoints}>
                            <div className="form-group">
                                <label htmlFor="pointsToAdd">{isOnChain ? `Amount (${selectedProgram.asa_unit_name || 'units'})` : 'Points to Add'}:</label>
                                <input
                                    type="text" id="pointsToAdd" name="pointsToAdd"
                                    value={pointsToAdd} onChange={handlePointsChange}
                                    placeholder={isOnChain ? 'e.g., 1.5' : 'e.g., 10'} required
                                    disabled={isSubmittingPoints}
                                />
                            </div>
                            <button
                                type="submit" className="submit-points-button"
                                disabled={isSubmittingPoints || !pointsToAdd || !(parseFloat(pointsToAdd) > 0)}
                            >
                                {isSubmittingPoints
                                    ? 'Submitting...'
                                    : isOnChain
                                        ? `Send ${pointsToAdd || '...'} ${selectedProgram.asa_unit_name || 'units'}`
                                        : `Add ${pointsToAdd || '...'} Points`}
                            </button>
                        </form>
                        {submitMessage && ( <p className={`submit-message ${submitMessage.toLowerCase().startsWith('error:') ? 'error-message' : 'success-message'}`}> {submitMessage} </p> )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScanQRPage;
